'use no memo';

import type { ColumnDef } from '@tanstack/react-table';
import type { Dispatch, SetStateAction } from 'react';

import { cn } from '@repo/lib/cn';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CheckIcon, PlusIcon } from 'lucide-react';
import { createContext, use, useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@/components/ui/table';

/**
 * Props for TanstackTableDynamic.
 * @property {ColumnDef<TData, TValue>[]} columns - Table column definitions.
 * @property {TData} newRowTemplate - Template for a new row.
 * @property {TData[]} data - Table data.
 * @property {Dispatch<SetStateAction<TData[]>>} setData - Setter for table data.
 * @property {(rows: TData[]) => void} [getSelectedRows] - Optional callback for selected rows.
 */
export interface DataTableDynamicProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  newRowTemplate: TData;
  data: TData[];
  setData: Dispatch<SetStateAction<TData[]>>;
  getSelectedRows?: (rows: TData[]) => void;
}

/** Record of edited row states by row ID. */
export type EditableRow = Record<string, boolean>;

/**
 * Context type for TanstackTableDynamic.
 */
interface TanstackTableDynamicContextType {
  editedRows: EditableRow;
  setEditedRows: Dispatch<SetStateAction<EditableRow>>;
  addRow: () => void;
  removeRow: (rowIndex: number) => void;
  updateData: (rowIndex: number, columnId: string, value: unknown) => void;
  revertData: (rowIndex: number) => void;
  saveChanges: (rowIndex: number) => void;
  restoreOriginalData: () => void;
}

const TanstackTableDynamicContext = createContext<TanstackTableDynamicContextType>({
  editedRows: {},
  setEditedRows: () => {},
  addRow: () => {},
  removeRow: () => {},
  updateData: () => {},
  revertData: () => {},
  saveChanges: () => {},
  restoreOriginalData: () => {},
});

export const useTanstackTableDynamicContext = () => {
  return use(TanstackTableDynamicContext);
};

export default function TanstackTableDynamic<TData, TValue>(dataTableDynamicProps: DataTableDynamicProps<TData, TValue>) {
  const { columns, newRowTemplate, data, setData } = dataTableDynamicProps;
  const [originalData, setOriginalData] = useState<TData[]>([]);
  const [editedRows, setEditedRows] = useState<EditableRow>({});
  const [newlyAddedRowIndex, setNewlyAddedRowIndex] = useState<number | null>(null);
  const [added, setAdded] = useState<boolean>(false);
  const lastRowRef = useRef<HTMLTableRowElement>(null);

  const restoreOriginalData = () => {
    if (data.length !== originalData.length) {
      setOriginalData((curr) => [...curr, ...data]);
    }
  };

  const addRow = () => {
    const newIndex = data.length;
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
    setData([...data, newRowTemplate]);
    setOriginalData([...originalData, newRowTemplate]);
    setNewlyAddedRowIndex(newIndex);
  };

  const updateData = (rowIndex: number, columnId: string, value: unknown) => {
    setData((curr: TData[]) => {
      return curr.map((row: TData, index: number) => {
        if (index === rowIndex) {
          return {
            ...curr[rowIndex],
            [columnId]: value,
          } as TData;
        }
        return row;
      });
    });
  };

  const revertData = (rowIndex: number) => {
    setData((curr: TData[]) => curr.map((row, index) => (index === rowIndex ? (originalData[rowIndex] !== undefined ? originalData[rowIndex] : row) : row)));
  };

  const saveChanges = (rowIndex: number) => {
    setOriginalData((old) => old.map((row, index) => (index === rowIndex ? (data[rowIndex] !== undefined ? data[rowIndex] : row) : row)));
  };

  const removeRow = (rowIndex: number) => {
    setData((old) => old.filter((_row, index) => index !== rowIndex));
    setOriginalData((old) => old.filter((_row, index) => index !== rowIndex));
  };

  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
  });

  useEffect(() => {
    if (newlyAddedRowIndex !== null && lastRowRef.current) {
      lastRowRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      const timer = setTimeout(() => {
        setNewlyAddedRowIndex(null);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [newlyAddedRowIndex]);

  return (
    <TanstackTableDynamicContext
      value={{
        restoreOriginalData: restoreOriginalData,
        editedRows: editedRows,
        setEditedRows: setEditedRows,
        addRow: addRow,
        updateData: updateData,
        removeRow: removeRow,
        revertData: revertData,
        saveChanges: saveChanges,
      }}
    >
      <div className="border-border-primary bg-background relative h-full w-full overflow-x-auto rounded-xl border">
        <Table className={'h-fit text-center'}>
          <TableHeader className={'bg-background/90 sticky top-0 z-10 w-full overflow-x-auto border-b backdrop-blur-xs'}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead className={'p-1 text-center'} key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, index) => {
              const isNewRow = index === newlyAddedRowIndex;
              return (
                <TableRow key={row.id} ref={isNewRow ? lastRowRef : null} className={cn('duration-300', isNewRow && 'animate-in fade-in bg-success-accent hover:bg-success-accent-hover')}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter className="sticky bottom-0">
            <TableRow className="border-t">
              <TableCell colSpan={columns.length} className="p-0">
                <div className="sticky left-0 w-fit p-3">
                  <Button variant={'tertiary'} type={'button'} size="sm" className={'w-full max-w-44 transition-transform duration-500'} onClick={addRow}>
                    <div className={cn('transition-all', added ? 'scale-100 opacity-100' : 'scale-0 opacity-0')}>
                      <CheckIcon className="stroke-icon-success" aria-hidden="true" />
                    </div>
                    <div className={cn('absolute left-2.5 transition-all', added ? 'scale-0 opacity-0' : 'scale-100 opacity-100')}>
                      <PlusIcon aria-hidden="true" />
                    </div>
                    Dodaj
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>
    </TanstackTableDynamicContext>
  );
}
